import { SNAPSHOT_SCHEMA_VERSION } from "@radar-sim/schema";
import type {
  Command,
  CommandResult,
  EnvironmentState,
  Event,
  LatLon,
  OwnShipState,
  SartState,
  Scenario,
  TargetState,
  TimedEvent,
  Vessel,
  Waypoint,
  WorldSnapshot,
} from "@radar-sim/schema";
import {
  instantiateBehaviour,
  type AreaDataLookup,
  type BehaviourPose,
  type BehaviourRuntime,
} from "./behaviours/index.js";
import { routeAroundLand, type IsOnLand } from "./coastline-router.js";
import { projectPosition } from "./geodesy.js";
import type {
  ConnectionState,
  RadarStateSource,
  Unsubscribe,
} from "./state-source.js";

export interface LocalSimSourceOptions {
  /** Snapshot rate in Hz. Fixed for the source's lifetime. Default 10. */
  tickRateHz?: number;
  /**
   * If false, `connect()` emits the initial snapshot but does not start an
   * interval-driven tick loop; the consumer drives ticks via `step()`. Used
   * by tests for deterministic stepping and by replay fast-forwarding.
   * Default true.
   */
  autoTick?: boolean;
  /** Area-data lookup used by behaviours that resolve laneId etc. */
  areaData?: AreaDataLookup;
  /**
   * Coastline predicate. When supplied, tracked-vessel waypoints are
   * routed around land at scenario load — segments crossing land get
   * intermediate waypoints inserted to deflect the path back into the
   * channel. Without this, vessels follow whatever waypoints the
   * scenario JSON specifies (which may cross land, hence the run-time
   * guarantee broken). The router is deterministic and runs once at
   * construction, so the determinism contract on the snapshot stream
   * is preserved.
   */
  isOnLand?: IsOnLand;
  /**
   * Tidal anomalies for the area — disturbed-water polygons with
   * optional set/drift. When own ship's track passes through an
   * anomaly, its position is displaced each tick by the local set
   * (severity-scaled). The host typically passes
   * `getTidalAnomalies(area)` from @radar-sim/area-data. Optional;
   * scenarios that don't need tidal modelling can omit it.
   */
  tidalAnomalies?: ReadonlyArray<{
    polygon: ReadonlyArray<LatLon>;
    severity: number;
    drift?: { bearingDeg: number; speedKnots: number };
  }>;
}

const DEFAULT_TICK_HZ = 10;
const SECONDS_PER_HOUR = 3600;

/**
 * In-process radar state source. Runs the deterministic simulation engine
 * directly; serves snapshots to local consumers via `subscribe`.
 *
 * Determinism contract: given identical scenario JSON and no commands, two
 * instances must produce byte-identical `WorldSnapshot` streams when stepped
 * the same number of times. See ADR-0001.
 */
export class LocalSimSource implements RadarStateSource {
  readonly mode = "solo" as const;
  readonly tickRateHz: number;

  private readonly autoTick: boolean;
  private readonly startWallClockMs: number;
  private readonly areaData: AreaDataLookup | undefined;
  private readonly isOnLand: IsOnLand | undefined;
  private readonly tidalAnomalies: ReadonlyArray<{
    polygon: ReadonlyArray<LatLon>;
    severity: number;
    drift?: { bearingDeg: number; speedKnots: number };
  }>;
  private readonly scenarioId: string;

  private connectionState: ConnectionState = "idle";
  private simSeconds = 0;
  private timeScale = 1;

  private ownShip: OwnShipState;
  private environment: EnvironmentState;
  private readonly vessels = new Map<string, Vessel>();
  private readonly behaviouralRuntimes = new Map<string, BehaviourRuntime>();
  /** Last computed pose per behavioural vessel; fed to inter-vessel behaviours. */
  private readonly behaviouralPoseCache = new Map<string, BehaviourPose>();
  /** Last computed pose for tracked vessels too — used by colregs targets. */
  private readonly trackedPoseCache = new Map<string, BehaviourPose>();
  private pendingEvents: TimedEvent[];
  private eventsSinceLast: Event[] = [];
  /** Active SART transponders. Once a `mob_drop` event fires, the SART
   *  stays in this list for the rest of the session — battery-powered
   *  beacons run for ~12-24h on a real raft, well past any teaching
   *  scenario duration. Recovery (the casualty being picked up) is a
   *  scoring concern, not an engine state concern. */
  private sarts: SartState[] = [];
  /**
   * Per-vessel cumulative tidal-drift offset in lat/lon degrees. We
   * keep this off the deterministic waypoint pose so a tracked
   * vessel's "intended" arrival times stay intact — the drift is a
   * separate displacement applied at snapshot-build time. Persists
   * for the vessel's lifetime so a vessel transiting a current zone
   * has its position carried forward correctly.
   */
  private vesselDriftOffset = new Map<string, { dLat: number; dLon: number }>();
  private lastDriftSimSeconds = 0;
  private sartCounter = 0;

  private readonly snapshotHandlers = new Set<(s: WorldSnapshot) => void>();
  private readonly stateHandlers = new Set<(s: ConnectionState) => void>();

  private intervalHandle: ReturnType<typeof setInterval> | undefined;

  constructor(scenario: Scenario, opts: LocalSimSourceOptions = {}) {
    this.tickRateHz = opts.tickRateHz ?? DEFAULT_TICK_HZ;
    this.autoTick = opts.autoTick ?? true;
    this.areaData = opts.areaData;
    this.isOnLand = opts.isOnLand;
    this.tidalAnomalies = opts.tidalAnomalies ?? [];
    this.scenarioId = scenario.metadata.id;
    this.startWallClockMs = Date.parse(
      scenario.initialConditions.simStartWallClock,
    );

    this.ownShip = { ...scenario.initialConditions.ownShip };
    this.environment = cloneEnvironment(
      scenario.initialConditions.environment,
    );

    for (const vessel of scenario.vessels) {
      this.addVessel(vessel, 0);
    }

    this.pendingEvents = [...scenario.events].sort(
      (a, b) => a.atSimSeconds - b.atSimSeconds,
    );
  }

  get state(): ConnectionState {
    return this.connectionState;
  }

  async connect(): Promise<void> {
    if (
      this.connectionState === "active" ||
      this.connectionState === "paused"
    ) {
      return;
    }
    this.transitionState("active");
    // Events scheduled at simSeconds=0 fire into the initial snapshot.
    this.fireDueEvents(Number.NEGATIVE_INFINITY, 0);
    this.emitSnapshot();
    if (this.autoTick) {
      const intervalMs = 1000 / this.tickRateHz;
      this.intervalHandle = setInterval(() => this.tick(), intervalMs);
    }
  }

  async disconnect(): Promise<void> {
    if (this.intervalHandle !== undefined) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = undefined;
    }
    this.transitionState("disconnected");
  }

  subscribe(handler: (snapshot: WorldSnapshot) => void): Unsubscribe {
    this.snapshotHandlers.add(handler);
    return () => {
      this.snapshotHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: ConnectionState) => void): Unsubscribe {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  async sendCommand(command: Command): Promise<CommandResult> {
    switch (command.kind) {
      case "pause":
        if (this.connectionState === "active") this.transitionState("paused");
        return { ok: true };
      case "resume":
        if (this.connectionState === "paused") this.transitionState("active");
        return { ok: true };
      case "set_time_scale":
        this.timeScale = command.scale;
        return { ok: true };
      case "set_own_cog":
        this.ownShip = { ...this.ownShip, cog: command.cog };
        return { ok: true };
      case "set_own_sog":
        this.ownShip = { ...this.ownShip, sog: command.sog };
        return { ok: true };
      case "inject_event":
        this.insertEvent(command.timed);
        return { ok: true };
      case "acquire_target":
      case "drop_target":
        return this.vessels.has(command.vesselId)
          ? { ok: true }
          : {
              ok: false,
              code: "unknown_target",
              reason: `unknown vessel id ${command.vesselId}`,
            };
    }
  }

  /**
   * Advance the simulation by exactly one tick and emit a snapshot.
   * No-op when not active. Public so tests and replay-style consumers can
   * drive the loop deterministically without engaging auto-tick.
   */
  step(): void {
    this.tick();
  }

  private tick(): void {
    if (this.connectionState !== "active") return;
    const dt = this.timeScale / this.tickRateHz;
    if (dt <= 0) {
      this.emitSnapshot();
      return;
    }
    const prev = this.simSeconds;
    const next = prev + dt;
    this.ownShip = advanceOwnShip(this.ownShip, dt);
    // Apply tidal set/drift: if the post-advance position is inside
    // any tidal-anomaly polygon with a declared drift, displace by
    // the local set scaled by severity and dt. Strongest case
    // (severity=1.0, 3 kn drift) at full speed gives ~1.5 m of
    // displacement per 100 ms tick — invisible per tick but
    // noticeable over a few minutes.
    this.ownShip = applyTidalDrift(this.ownShip, dt, this.tidalAnomalies);
    this.fireDueEvents(prev, next);
    this.simSeconds = next;
    this.emitSnapshot();
  }

  private addVessel(vessel: Vessel, atSimSeconds: number): void {
    // Tracked vessels get their waypoint path routed around land at
    // load time. The transformation is deterministic (pure function of
    // waypoints + the supplied isOnLand predicate) and matches the
    // vessel's original arrival schedule — intermediate waypoints
    // share the line-distance fraction so the vessel speed stays
    // consistent. Determinism contract on the snapshot stream is
    // preserved because two LocalSimSource instances with the same
    // inputs route identically.
    const stored: Vessel =
      vessel.kind === "tracked" && this.isOnLand !== undefined
        ? {
            ...vessel,
            waypoints: routeAroundLand(vessel.waypoints, this.isOnLand),
          }
        : vessel;
    this.vessels.set(stored.id, stored);
    if (stored.kind === "behavioural") {
      const runtime = instantiateBehaviour(stored.behaviour.name, {
        params: stored.behaviour,
        spawn: { ...stored.spawn },
        vesselId: stored.id,
        scenarioId: this.scenarioId,
        spawnSimSeconds: atSimSeconds,
      });
      this.behaviouralRuntimes.set(stored.id, runtime);
      this.behaviouralPoseCache.set(stored.id, { ...stored.spawn });
    }
  }

  private removeVessel(id: string): void {
    this.vessels.delete(id);
    this.behaviouralRuntimes.delete(id);
    this.behaviouralPoseCache.delete(id);
    this.trackedPoseCache.delete(id);
    this.vesselDriftOffset.delete(id);
  }

  private insertEvent(timed: TimedEvent): void {
    let i = 0;
    while (
      i < this.pendingEvents.length &&
      this.pendingEvents[i]!.atSimSeconds <= timed.atSimSeconds
    ) {
      i++;
    }
    this.pendingEvents.splice(i, 0, timed);
  }

  private fireDueEvents(afterSeconds: number, throughSeconds: number): void {
    // Drain any events whose time has come (or whose time was already
    // in the past — happens when an inject_event command carries a
    // stale atSimSeconds because the client computed it against an
    // older snapshot). Shift-and-fire ensures each event runs at most
    // once; the > afterSeconds guard the old code used would let a
    // stale event sit at the head of the queue and block every event
    // behind it from ever firing.
    void afterSeconds;
    while (
      this.pendingEvents.length > 0 &&
      this.pendingEvents[0]!.atSimSeconds <= throughSeconds
    ) {
      const next = this.pendingEvents.shift()!;
      this.applyEvent(next.event, next.atSimSeconds);
    }
  }

  private applyEvent(event: Event, atSimSeconds: number): void {
    this.eventsSinceLast.push(event);
    switch (event.kind) {
      case "rain_cell_appears":
        this.environment = {
          ...this.environment,
          rainCells: [...this.environment.rainCells, { ...event.cell }],
        };
        return;
      case "rain_cell_dissipates":
        this.environment = {
          ...this.environment,
          rainCells: this.environment.rainCells.filter(
            (c) => c.id !== event.cellId,
          ),
        };
        return;
      case "sea_state_changes":
        // Smooth transition modeling lands with environment effects (week 3-4).
        this.environment = { ...this.environment, seaState: event.toState };
        return;
      case "visibility_changes":
        this.environment = { ...this.environment, visibilityNm: event.toNm };
        return;
      case "vessel_appears":
        this.addVessel(event.vessel, atSimSeconds);
        return;
      case "vessel_disappears":
        this.removeVessel(event.vesselId);
        return;
      case "mob_drop": {
        // Convert the relative drop position (asternNm + abeam) to an
        // absolute lat/lon at the moment the event fires. Two short
        // projection legs from own ship: one along the reciprocal of
        // current heading for the astern offset, then perpendicular
        // (port = -90°, starboard = +90° relative to heading) for the
        // abeam offset. Heading is in compass degrees, so projection
        // gets the right answer directly.
        const asternBearing = (this.ownShip.heading + 180) % 360;
        const aft = projectPosition(
          this.ownShip.position,
          asternBearing,
          event.relativeToOwn.asternNm,
        );
        const beamOffsetDeg =
          event.relativeToOwn.abeamSide === "port" ? -90 : 90;
        const abeamBearing =
          (this.ownShip.heading + beamOffsetDeg + 360) % 360;
        const sartPosition = projectPosition(
          aft,
          abeamBearing,
          event.relativeToOwn.abeamNm,
        );
        this.sartCounter += 1;
        this.sarts.push({
          id: `sart-${this.sartCounter}`,
          position: sartPosition,
          activatedAtSimSeconds: atSimSeconds,
        });
        return;
      }
      case "racon_activates":
      case "equipment_failure":
        // Surfaced via eventsSinceLast; RACON paint comes from the
        // RACON definitions in area-data and is handled in the renderer.
        // Equipment failure modeling is out of scope for v1.
        return;
    }
  }

  private buildSnapshot(): WorldSnapshot {
    // Accumulate per-vessel tidal drift since the last snapshot.
    // Done up-front (rather than per-vessel inside the loop) so each
    // vessel's drift is computed against its own pose at this tick,
    // independent of order.
    const dt = this.simSeconds - this.lastDriftSimSeconds;
    this.lastDriftSimSeconds = this.simSeconds;

    const targets: TargetState[] = [];
    for (const vessel of this.vessels.values()) {
      const pose = this.computeVesselPose(vessel);
      // Apply tidal drift for this vessel: if its current pose sits
      // inside a tidal-anomaly polygon, accumulate severity-scaled
      // drift into a per-vessel offset, then displace the rendered
      // position by that offset. Tracked-vessel arrival times stay
      // unaffected (the offset is a positional displacement, not a
      // time shift).
      let displaced = pose.position;
      if (this.tidalAnomalies.length > 0 && dt > 0) {
        const offset = this.vesselDriftOffset.get(vessel.id) ?? {
          dLat: 0,
          dLon: 0,
        };
        for (const a of this.tidalAnomalies) {
          if (a.drift === undefined) continue;
          if (!pointInPolygon(pose.position, a.polygon)) continue;
          const speedKn = a.drift.speedKnots * a.severity;
          const distNm = (speedKn * dt) / 3600;
          if (distNm <= 0) break;
          // Convert distance/bearing to dlat/dlon and accumulate.
          const rad = (a.drift.bearingDeg * Math.PI) / 180;
          const dLat = (distNm / 60) * Math.cos(rad);
          const dLon =
            (distNm /
              (60 * Math.cos((pose.position.lat * Math.PI) / 180))) *
            Math.sin(rad);
          offset.dLat += dLat;
          offset.dLon += dLon;
          this.vesselDriftOffset.set(vessel.id, offset);
          break; // only the first matching anomaly applies
        }
        if (offset.dLat !== 0 || offset.dLon !== 0) {
          displaced = {
            lat: pose.position.lat + offset.dLat,
            lon: pose.position.lon + offset.dLon,
          };
        }
      }
      const target: TargetState = {
        id: vessel.id,
        vesselType: vessel.vesselType,
        position: displaced,
        cog: pose.cog,
        sog: pose.sog,
        rcs: vessel.rcs,
        visible: true,
      };
      if (vessel.ais !== undefined) {
        target.ais = vessel.ais;
      }
      if (vessel.state !== undefined) {
        target.state = vessel.state;
      }
      targets.push(target);
    }
    const wallClockMs = this.startWallClockMs + this.simSeconds * 1000;
    return {
      schemaVersion: SNAPSHOT_SCHEMA_VERSION,
      simTimeSeconds: this.simSeconds,
      wallClock: new Date(wallClockMs).toISOString(),
      ownShip: { ...this.ownShip },
      targets,
      sarts: this.sarts.map((s) => ({ ...s })),
      environment: cloneEnvironment(this.environment),
      eventsSinceLast: [...this.eventsSinceLast],
    };
  }

  private emitSnapshot(): void {
    const snap = this.buildSnapshot();
    this.eventsSinceLast = [];
    for (const handler of this.snapshotHandlers) {
      handler(snap);
    }
  }

  private transitionState(next: ConnectionState): void {
    if (this.connectionState === next) return;
    this.connectionState = next;
    for (const handler of this.stateHandlers) {
      handler(next);
    }
  }

  private computeVesselPose(vessel: Vessel): VesselPose {
    if (vessel.kind === "tracked") {
      const pose = interpolateTrackedVessel(vessel.waypoints, this.simSeconds);
      this.trackedPoseCache.set(vessel.id, pose);
      return pose;
    }
    const runtime = this.behaviouralRuntimes.get(vessel.id);
    if (runtime === undefined) {
      // Defensive: behaviour failed to register. Treat as stationary at spawn.
      return { ...vessel.spawn };
    }
    const otherVessels = new Map<string, BehaviourPose>();
    for (const [id, pose] of this.behaviouralPoseCache) {
      if (id !== vessel.id) otherVessels.set(id, pose);
    }
    for (const [id, pose] of this.trackedPoseCache) {
      otherVessels.set(id, pose);
    }
    const pose = runtime.poseAt(this.simSeconds, {
      otherVessels,
      areaData: this.areaData,
    });
    this.behaviouralPoseCache.set(vessel.id, pose);
    return pose;
  }
}

interface VesselPose {
  position: LatLon;
  cog: number;
  sog: number;
}

/** Apply tidal set/drift if the own-ship position is inside an
 *  anomaly polygon. Pure function of the inputs — same own-ship +
 *  same anomalies + same dt always returns the same output. */
function applyTidalDrift(
  own: OwnShipState,
  dtSeconds: number,
  anomalies: ReadonlyArray<{
    polygon: ReadonlyArray<LatLon>;
    severity: number;
    drift?: { bearingDeg: number; speedKnots: number };
  }>,
): OwnShipState {
  if (anomalies.length === 0 || dtSeconds <= 0) return own;
  // Find the first anomaly whose polygon contains the own-ship
  // position. Multiple overlapping anomalies are unusual for the
  // areas we model; pick the first match.
  for (const a of anomalies) {
    if (a.drift === undefined) continue;
    if (!pointInPolygon(own.position, a.polygon)) continue;
    const speedKn = a.drift.speedKnots * a.severity;
    const distNm = (speedKn * dtSeconds) / 3600;
    if (distNm <= 0) return own;
    return {
      ...own,
      position: projectPosition(own.position, a.drift.bearingDeg, distNm),
    };
  }
  return own;
}

function pointInPolygon(
  p: LatLon,
  polygon: ReadonlyArray<LatLon>,
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const intersect =
      a.lat > p.lat !== b.lat > p.lat &&
      p.lon < ((b.lon - a.lon) * (p.lat - a.lat)) / (b.lat - a.lat) + a.lon;
    if (intersect) inside = !inside;
  }
  return inside;
}

function advanceOwnShip(own: OwnShipState, dtSeconds: number): OwnShipState {
  if (own.sog === 0 || dtSeconds === 0) return { ...own };
  const distNm = own.sog * (dtSeconds / SECONDS_PER_HOUR);
  const next = projectPosition(own.position, own.cog, distNm);
  return { ...own, position: next };
}

function interpolateTrackedVessel(
  waypoints: readonly Waypoint[],
  simSeconds: number,
): VesselPose {
  // Schema guarantees: ≥2 waypoints, first at simSeconds 0, strictly
  // increasing time order. Bounds checks below are defensive.
  const first = waypoints[0]!;
  if (simSeconds <= first.atSimSeconds) {
    return { position: { ...first.position }, cog: first.cog, sog: first.sog };
  }
  const last = waypoints[waypoints.length - 1]!;
  if (simSeconds >= last.atSimSeconds) {
    // Past the last waypoint: dead-reckon at the last waypoint's cog/sog.
    const elapsed = simSeconds - last.atSimSeconds;
    const distNm = last.sog * (elapsed / SECONDS_PER_HOUR);
    return {
      position:
        distNm > 0
          ? projectPosition(last.position, last.cog, distNm)
          : { ...last.position },
      cog: last.cog,
      sog: last.sog,
    };
  }
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i]!;
    const b = waypoints[i + 1]!;
    if (simSeconds >= a.atSimSeconds && simSeconds <= b.atSimSeconds) {
      const span = b.atSimSeconds - a.atSimSeconds;
      const t = span === 0 ? 0 : (simSeconds - a.atSimSeconds) / span;
      const lat = a.position.lat + (b.position.lat - a.position.lat) * t;
      const lon = a.position.lon + (b.position.lon - a.position.lon) * t;
      return { position: { lat, lon }, cog: a.cog, sog: a.sog };
    }
  }
  return { position: { ...last.position }, cog: last.cog, sog: last.sog };
}

function cloneEnvironment(env: EnvironmentState): EnvironmentState {
  return {
    ...env,
    rainCells: env.rainCells.map((c) => ({ ...c })),
  };
}
